import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import _ from 'lodash';

import MetricsGate from 'components/MetricsGate';

import ScorecardShow from './ScorecardShow';
import PopupProvider from '../../contextProviders/PopupProvider';
import ScorecardProvider from '../../contextProviders/ScorecardProvider';
import AnalyticsContext from '../../contexts/AnalyticsContext';
import usePopup from '../../hooks/usePopup';
import useOpenKpiPopup from 'components/Scorecard/Kpis/hooks/useOpenKpiPopup';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import ScorecardForm from '../ScorecardsIndex/ScorecardForm';
import useWindowSize from '../../hooks/useWindowSize';
import ScorecardContext from '../../contexts/ScorecardContext';
import WorkSpacesContext from '../../contexts/WorkSpacesContext';
import WorkSpaceContext from '../../contexts/WorkSpaceContext';
import TasksProvider from 'contextProviders/TasksProvider/LocalTasksProvider';
import useContentViewTracking from '../../hooks/useContentViewTracking';
import LocalTimelineProvider from 'contextProviders/TimelineProvider/LocalTimelineProvider';
import { SCORECARD_RESOURCE } from '../../constants';
import TargetsAppContext from 'screens/TargetsAppShow/TargetsAppConfigured/TargetsAppContext';
import NavSideBarContext from 'contexts/NavSideBarContext';
import { OPENED_NAV_WIDTH, CLOSED_NAV_WIDTH } from 'navigation/styles';
import { isKpiRow } from 'hooks/kpiTypeCheckers';
import DashboardContext from 'contexts/DashboardContext';
import AppRoutes from '../../navigation/appRoutes';
import useRefreshDatasetFreshnessOnMount from '../../hooks/useRefreshDatasetFreshnessOnMount';
import { UIVersionContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/UIVersionProvider';
import appRoutes from '../../navigation/appRoutes';
import EntityDetailsContext from '../EntityDetailsShow/EntityDetailsContext';
import useHasAccess from '../../hooks/useHasAccess';

const usePopupKpiQueryParams = () => {
  const { search } = useLocation();
  const [params] = useState(() => {
    const p = new URLSearchParams(search);
    const periodStart = p.get('periodStart');
    const periodEnd = p.get('periodEnd');
    const kpiId = p.get('kpiId');

    if (periodStart && periodEnd && kpiId) {
      return {
        periodStart,
        periodEnd,
        kpiId,
      };
    }
  });

  return params;
};

const useInitialPopup = () => {
  const { periods, scorecard } = useContext(ScorecardContext);
  const params = usePopupKpiQueryParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [kpi, setKpi] = useState<Scorecards.ScorecardKpi>();
  const [period, setPeriod] = useState<Period>();
  const [hasOpenedPopup, setHasOpenedPopup] = useState<boolean>(false);

  const openPopUp = useOpenKpiPopup(kpi, period);

  useEffect(() => {
    if (!scorecard || !params) {
      return;
    }

    setKpi(scorecard.kpis.filter(isKpiRow).find((k) => params.kpiId === k.id));
    setPeriod(
      periods.find(
        (p) =>
          p.startDate === params.periodStart && p.endDate === params.periodEnd,
      ),
    );
  }, [params, periods, scorecard]);

  useEffect(() => {
    if (!!openPopUp && !hasOpenedPopup) {
      openPopUp();
      navigate(location.pathname, { replace: true });
      setHasOpenedPopup(true);
    }
  }, [hasOpenedPopup, location.pathname, navigate, openPopUp]);

  return;
};

const useRedirectIfNoAccess = ({
  scorecard,
}: {
  scorecard: Scorecards.Scorecard;
}) => {
  const navigate = useNavigate();
  const { workSpace } = useContext(WorkSpaceContext);
  const { dashboard } = useContext(DashboardContext);
  const entityDetailsContext = useContext(EntityDetailsContext);
  const hasAccess = useHasAccess();

  useEffect(() => {
    if (!!dashboard || !!workSpace || !!entityDetailsContext) {
      return;
    }
    if (
      !hasAccess({
        resource: scorecard,
        type: 'scorecard',
        typeId: scorecard.id,
        access: scorecard.access,
      })
    ) {
      navigate(appRoutes.home);
    }
  }, [
    entityDetailsContext,
    workSpace,
    hasAccess,
    navigate,
    dashboard,
    scorecard,
  ]);
};

export const ScorecardShowContainer = ({
  isInWorkSpace,
}: {
  isInWorkSpace?: boolean;
}) => {
  const { scorecard, kpisListDivParentScrollerRef } =
    useContext(ScorecardContext);
  useRedirectIfNoAccess({ scorecard });
  const { workSpace } = useContext(WorkSpaceContext);
  const { dashboard } = useContext(DashboardContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { allWorkSpaces } = useContext(WorkSpacesContext);
  const { isOpen: isNavOpen } = useContext(NavSideBarContext);
  const isInTargets = useContext(TargetsAppContext);
  useContentViewTracking({
    type: 'scorecard',
    typeId: scorecard.id,
    name: scorecard.title,
  });
  useRefreshDatasetFreshnessOnMount();
  useInitialPopup();
  const navigate = useNavigate();
  const [taskAssignableUserIds, setTaskAssignableUserIds] = useState<string[]>(
    [],
  );
  const [hasTrackedOpen, setHasTrackedOpen] = useState<boolean>(false);

  const [taskSources, setTaskSources] = useState<Tasks.TaskSource[]>([]);
  const [taskDestination, setTaskDestination] = useState<
    Tasks.TaskSource | undefined
  >();

  useEffect(() => {
    setTaskSources([
      {
        type: SCORECARD_RESOURCE,
        id: scorecard.id,
      },
    ]);

    setTaskDestination({
      type: SCORECARD_RESOURCE,
      id: scorecard.id,
    });
  }, [scorecard.id]);

  useEffect(() => {
    const newAssignableIds = [];
    newAssignableIds.push(...scorecard.assigneeId);

    setTaskAssignableUserIds(_.uniq(newAssignableIds));
  }, [scorecard.assigneeId, allWorkSpaces, scorecard.id]);

  const { isMobile } = useWindowSize();
  const { open: openEdit, close: closeEdit, isOpen: isEditOpen } = usePopup();

  const onEditClicked = useCallback(() => {
    openEdit();
    trackEvent('KPI List - Edit Clicked');
  }, [openEdit, trackEvent]);

  useEffect(() => {
    if (hasTrackedOpen) {
      return;
    }
    trackEvent('KPI List - Opened', {
      scorecardId: scorecard.id,
      scorecardName: scorecard.title,
      source: workSpace ? 'Workspace' : dashboard ? 'Dashboard' : 'Scorecard',
    });
    setHasTrackedOpen(true);
  }, [
    dashboard,
    hasTrackedOpen,
    scorecard.id,
    scorecard.title,
    trackEvent,
    workSpace,
  ]);

  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <div
      style={{
        width:
          isInWorkSpace || !!isInTargets
            ? '100%'
            : isNavOpen
              ? `calc(100% - ${OPENED_NAV_WIDTH}px)`
              : `calc(100% - ${CLOSED_NAV_WIDTH}px)`,
      }}
    >
      <TasksProvider
        destination={taskDestination}
        sources={taskSources}
        assignableUserIds={taskAssignableUserIds}
      >
        <ScorecardShow
          goBack={goBack}
          scorecard={scorecard}
          onEditScorecardClicked={onEditClicked}
          isMobile={isMobile}
          kpisListDivParentScrollerRef={kpisListDivParentScrollerRef}
          isInWorkSpace={isInWorkSpace}
          isInTargets={!!isInTargets}
        />
      </TasksProvider>

      <ModalTransition>
        {isEditOpen && (
          <Modal shouldScrollInViewport={false} autoFocus={false}>
            <ScorecardForm
              close={closeEdit}
              scorecard={scorecard}
              isCopyingScorecard={false}
            />
          </Modal>
        )}
      </ModalTransition>
    </div>
  );
};

export const ScorecardMetricsGate = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { scorecard } = useContext(ScorecardContext);
  return (
    <MetricsGate content={scorecard} contentType="Scorecard">
      {children}
    </MetricsGate>
  );
};

const Gate = ({ scorecardIdProp }: { scorecardIdProp?: string }) => {
  const { scorecardId: scorecardIdParam } = useParams<{
    scorecardId: string;
    scorecardTabId?: string;
    workspaceId?: string;
  }>();
  const { uiVersion } = useContext(UIVersionContext);
  const scorecardId = scorecardIdProp ? scorecardIdProp : scorecardIdParam;
  const navigate = useNavigate();
  const [sources, setSources] = useState<Timeline.EventSource[]>([]);
  const [destination, setDestination] = useState<Timeline.EventSource>();
  const { workSpace } = useContext(WorkSpaceContext);
  const targetsAppContext = useContext(TargetsAppContext);

  useEffect(() => {
    if (!scorecardId) {
      return;
    }
    setSources([{ type: SCORECARD_RESOURCE, id: scorecardId }]);
    setDestination({ type: SCORECARD_RESOURCE, id: scorecardId });
  }, [scorecardId]);

  if (!scorecardId) {
    navigate(AppRoutes.home);
    return null;
  }

  return (
    <ScorecardProvider
      scorecardId={scorecardId}
      key={scorecardId}
      isAddKpiEnabled
      isDragAndDropEnabled
    >
      <ScorecardMetricsGate>
        <LocalTimelineProvider sources={sources} destination={destination}>
          <PopupProvider>
            <ScorecardShowContainer
              isInWorkSpace={
                !!workSpace || !!targetsAppContext || uiVersion === 'v3'
              }
            />
          </PopupProvider>
        </LocalTimelineProvider>
      </ScorecardMetricsGate>
    </ScorecardProvider>
  );
};

export default Gate;
